import {
  Component,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import { Router } from '@angular/router';
import { ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { animate, style, transition, trigger } from '@angular/animations';

import { TranslateModule } from '@ngx-translate/core';
import { DeviceDetectorService } from 'ngx-device-detector';

import { catchError, of } from 'rxjs';

import { ERoutes } from 'src/app/core/enums/routes.enum';
import { EScrollPrefix } from 'src/app/core/enums/scroll-to.enum';
import { UserProfile } from 'src/app/core/interfaces/user-profile';
import { ITripRoute } from 'src/app/core/types/interfaces/popular-trip.interface';
import { IMapTripDestination } from 'src/app/core/types/interfaces/trip-elements.interface';

import { AuthService } from 'src/app/core/services/auth.service';
import { SsrHelperService } from 'src/app/core/services/ssr-helper.service';
import { ScrollToIdService } from 'src/app/core/services/scroll-to-id.service';
import { AiAssistantService } from 'src/app/core/services/ai-assistant.service';
import { TripWizardStateService } from '../trip-wizard/trip-wizard-state.service';

import { Trip } from '../../classes/trip';
import { PreviewMapComponent } from '../../maps/preview-map/preview-map.component';
import { TripPlanPrintComponent } from '../trip-plan-print/trip-plan-print.component';
import { GoogleAnalyticService } from '../../../core/services/google-analytic.service';

export interface IChatCity {
  place_id: number;
  country_id: number;
  days_in_city: number;
  name: string;
}

export interface IChatMessage {
  question: string;
  answer: string;
  cities: IChatCity[];
  preferences: string[];
  typewriting: boolean;
}

@Component({
    selector: 'app-ai-chat-dialog',
    templateUrl: './ai-chat-dialog.component.html',
    styleUrls: ['./ai-chat-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger('questionInAnimation', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(25px)' }),
                animate('400ms 50ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
        ]),
        trigger('answerInAnimation', [
            transition(':enter', [
                style({ opacity: 0, transform: 'translateY(25px)' }),
                animate('400ms 450ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
            ]),
        ]),
    ],
    imports: [
        TranslateModule,
        ReactiveFormsModule,
        PreviewMapComponent,
        TripPlanPrintComponent,
    ]
})
export class AiChatDialogComponent implements OnInit {
  readonly MAX_STRING_LENGTH: number = 200;
  readonly examples: string[] = [
    'Plan a 12 day trip from Rome to Milan',
    'Plan a romantic winery trip to Tuscany',
  ];
  countryCoordinates: [number, number] = [12.4942486, 41.8905198];
  preferences: string[] = [];
  inputText = new UntypedFormControl('');
  userProfile: UserProfile | null = null;
  isCustomizeThisTripConfirmation = false;

  messages: IChatMessage[] = [];
  aiAssistant: { messages: IChatMessage[]; routes: IChatCity[] } = {
    messages: [],
    routes: [],
  };

  destinations: any[] = [];
  destinationsRoute: IMapTripDestination[] = [];

  @ViewChild('messagesList') messagesListContainer: ElementRef;

  private readonly tripState: WritableSignal<Trip>;

  #tripWizardStateService: TripWizardStateService = inject(
    TripWizardStateService,
  );

  constructor(
    private device: DeviceDetectorService,
    private scrollToIdService: ScrollToIdService,
    private aiAssistantService: AiAssistantService,
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly ssrHelperService: SsrHelperService,
    protected readonly googleAnalyticService: GoogleAnalyticService,
    private readonly dialogRef: MatDialogRef<AiChatDialogComponent>,
  ) {
    this.tripState = signal(this.#tripWizardStateService.currentTripStateValue);
  }

  get isDisabledCustomizeTripBtn(): boolean {
    const messages = this.messages;
    if (!messages.length) return true;
    return !messages[messages.length - 1].cities.length;
  }

  ngOnInit(): void {
    this.userProfile = this.authService.userProfile;
  }

  closeDialog(): void {
    this.dialogRef.close();
    this.googleAnalyticService.setCloseAiPlan({
      userID: this.authService.userId,
    });
  }

  autoGrow(element: HTMLElement): void {
    element.style.height = '72px';
    element.style.height = element.scrollHeight + 'px';
  }

  selectExampleText(exampleText: string, inputEl: HTMLElement): void {
    this.inputText.reset(exampleText);
    this.autoGrow(inputEl);
  }

  submitCustomizeAiTrip(): void {
    let duration = 0;
    const chatCities = this.aiAssistant.routes;
    const firstDestination = chatCities[0];
    const lastDestination = chatCities[chatCities.length - 1];
    const routes: ITripRoute[] = [];

    chatCities.forEach((r) => {
      duration += r.days_in_city;
      routes.push({
        coordinates: '0, 0',
        countryId: r.country_id,
        days: r.days_in_city,
        id: r.place_id,
        name: r.name,
      });
    });

    this.tripState.update((trip) =>
      trip.updateTrip({
        name: '',
        savesId: '',
        blogUrl: '',
        isOptimizedMode: false,
        startingLocationId: String(firstDestination.place_id),
        startingLabel: String(firstDestination.name),
        startingCountry: String(firstDestination.country_id),
        endingLocationId: String(lastDestination.place_id),
        endingLabel: String(lastDestination.name),
        endingCountry: String(lastDestination.country_id),
        numDays: Number(duration),
        route: routes,
        plannerInfo: {
          type: 'ai-optimizer',
          name: '',
          description: '',
        },
        includes: [],
        excludes: [],
      }),
    );

    this.#tripWizardStateService.currentTripState = new Trip({
      ...this.tripState(),
    });
    this.#tripWizardStateService.byUserInteraction = true;

    this.router
      .navigate([ERoutes.SLASH, ERoutes.TRIP_PLANNER])
      .then(() => this.dialogRef.close());
  }

  resetChat(): void {
    this.messages = [];

    this.inputText.reset('');

    this.aiAssistant.messages = [];
    this.isCustomizeThisTripConfirmation = false;

    this.destinationsRoute = [];
    this.preferences = [];

    if (
      this.messagesListContainer &&
      this.messagesListContainer.nativeElement
    ) {
      this.messagesListContainer.nativeElement.scrollTop = 0;
    }
  }

  sendMessage(): void {
    this.googleAnalyticService.setUseAiPlan({
      userID: this.authService.userId,
    });

    const messageText = this.inputText.value;
    const newMessage: IChatMessage = {
      question: messageText,
      answer: '',
      cities: [],
      preferences: [],
      typewriting: true,
    };
    this.messages.push(newMessage);
    this.inputText.reset('');

    if (this.device.isMobile()) {
      setTimeout(() => {
        this.scrollToIdService.scrollToItineraryInfo(
          this.messages.length - 1,
          EScrollPrefix.ELEMENT,
        );
      }, 100);
    } else {
      this.autoScrollToBottom(50);
    }

    this.aiAssistantService
      .sendMessage(messageText, this.preferences)
      .pipe(catchError((err) => of(err)))
      .subscribe((res) => {
        if (res && res.answer) {
          const answer = res.answer;
          let autoscrollCount = Math.floor(answer.length / 10);
          newMessage.cities = res.cities;
          newMessage.preferences = res.preferences;
          this.messages.splice(this.messages.length - 1, 1, newMessage);

          for (let i = 0; i <= answer.length; i++) {
            setTimeout(() => {
              newMessage.answer = answer.substring(0, i);

              if (i >= autoscrollCount) {
                autoscrollCount += answer.length / 10;
                this.autoScrollToBottom();
              }

              if (i === answer.length) {
                newMessage.typewriting = false;
                this.aiAssistant.messages = this.messages;
              }
            }, i * 10);
          }

          if (newMessage.cities.length) {
            this.aiAssistant.messages = this.messages;
            if (newMessage.cities.length === 1) {
              newMessage.cities.push(...newMessage.cities);
            }
            this.aiAssistantService
              .createAiRoute(res.cities, this.aiAssistant)
              .subscribe((destinations: any[]) => {
                this.destinations = destinations;
                this.getRouteCoordinates(destinations);
              });
          }
        } else {
          newMessage.answer = 'Oops, something went wrong, please try again..';
          newMessage.typewriting = false;
          this.messages.splice(this.messages.length - 1, 1, newMessage);
        }
      });
  }

  private autoScrollToBottom(delay = 150): void {
    setTimeout(() => {
      const messagesContainer = this.messagesListContainer.nativeElement;
      if (messagesContainer) {
        messagesContainer.scrollTo({
          top: messagesContainer.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, delay);
  }

  private getRouteCoordinates(route: any): void {
    if (!route) return;
    this.destinationsRoute = [];

    route.forEach((r: any) => {
      const parsedCityCoordinate = r.coordinates
        .split(',')
        .map((cs: string) => parseFloat(cs.trim()))
        .reverse();
      this.destinationsRoute.push({
        coordinates: parsedCityCoordinate,
        nextLegIsFlight: r.nextLegIsFlight,
      });
    });
  }
}
